import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Crown, Zap, Check, X, ChevronDown, ChevronUp,
  Shield, Clock, Headphones, Users, BarChart3, Database,
  Code, Sparkles, Target, TrendingUp, BadgeCheck, Globe, Infinity, Smartphone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStatusContext } from '../hooks/SubscriptionStatusContext';

// ==================== STYLED COMPONENTS ====================

const PageContainer = styled.div`
  min-height: 100vh;
  padding: ${props => props.theme.spacing[6]} 0;
  background: ${props => props.theme.colors.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing[6]};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 0 ${props => props.theme.spacing[4]};
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  border: none;
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: ${props => props.theme.spacing[6]};

  &:hover {
    color: ${props => props.theme.colors.textPrimary};
    transform: translateX(-4px);
  }
`;

// ==================== HERO SECTION ====================

const HeroSection = styled.section`
  text-align: center;
  padding: ${props => props.theme.spacing[12]} 0 ${props => props.theme.spacing[16]};
  position: relative;
  overflow: hidden;



  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: ${props => props.theme.spacing[8]} 0 ${props => props.theme.spacing[12]};
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing[4]};
`;

const HeroTitle = styled.h1`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['5xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  line-height: 1.2;
  max-width: 800px;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    font-size: ${props => props.theme.typography.fontSize['4xl']};
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize['3xl']};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.xl};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  max-width: 600px;
  line-height: 1.6;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize.lg};
  }
`;

const TrustBadges = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing[6]};
  margin-top: ${props => props.theme.spacing[6]};
  flex-wrap: wrap;
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.sm};

  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

// ==================== PRICING SECTION ====================

const PricingSection = styled.section`
  padding: ${props => props.theme.spacing[8]} 0;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing[6]};
  margin-top: ${props => props.theme.spacing[8]};
  align-items: stretch;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const PlanCard = styled(motion.div) <{ $featured?: boolean; $disabled?: boolean }>`
  height: 100%;
  background: ${props => props.theme.colors.backgroundSecondary};
  backdrop-filter: blur(10px);
  border: 1px solid ${props => props.$featured ? `${props.theme.colors.primary}60` : props.theme.colors.border};
  border-radius: 24px;
  padding: ${props => props.theme.spacing[10]} ${props => props.theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[8]};
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${props => props.$disabled ? 0.6 : 1};
  overflow: visible;

  ${props => props.$featured && `
    border-width: 2px;
    box-shadow: 0 0 20px -5px ${props.theme.colors.primary}40;
    
    &::before {
      content: '';
      position: absolute;
      inset: -2px;
      padding: 2px;
      border-radius: 24px;
      background: linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primary}40);
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }
  `}

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px -20px ${props => props.theme.colors.primary}40;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 8px 20px;
  border-radius: 99px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 10;
  white-space: nowrap;

  svg {
    color: #fbbf24;
  }
`;

const SavingsCornerBadge = styled.div<{ $color?: string }>`
  position: absolute;
  top: -12px;
  right: -12px;
  background: ${props => props.$color || props.theme.colors.primary};
  color: white;
  padding: 6px 14px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 10;
  transform: rotate(5deg);
`;

const PlanHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
  padding-bottom: ${props => props.theme.spacing[2]};
`;

const PlanName = styled.h3`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${props => props.theme.spacing[2]};
`;

const OldPrice = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textTertiary};
  text-decoration: line-through;
  margin-bottom: -4px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  flex-wrap: wrap;
`;

const Price = styled.span`
  font-size: ${props => props.theme.typography.fontSize['5xl']};
  font-weight: 800;
  color: ${props => props.theme.colors.textPrimary};
  line-height: 1.1;
  letter-spacing: -1px;
`;

const PricePeriod = styled.span`
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.textSecondary};
  margin-left: -4px;
`;

const InstallmentInfo = styled.div`
  font-size: 14px;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 4px;
  font-weight: 500;

  span {
    color: #3b82f6;
    font-weight: 600;
  }
`;

const PlanDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme.colors.textSecondary};
  margin: 4px 0 0 0;
  line-height: 1.4;
  font-weight: 400;
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[3]};
  flex: 1;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
`;

const FeatureIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.primary}20;
  color: ${props => props.theme.colors.primary};
  border-radius: 50%;
  flex-shrink: 0;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const FeatureText = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textPrimary};
  line-height: 1.6;
`;

const PlanButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  background: ${props => props.$variant === 'primary' ? props.theme.colors.primary : props.theme.colors.backgroundTertiary};
  color: ${props => props.$variant === 'primary' ? 'white' : props.theme.colors.textPrimary};
  border: 2px solid ${props => props.$variant === 'primary' ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
    background: ${props => props.$variant === 'primary' ? props.theme.colors.primaryHover : props.theme.colors.backgroundSecondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SavingsBadge = styled.div`
  background: ${props => props.theme.colors.primary}20;
  border: 1px solid ${props => props.theme.colors.primary}40;
  padding: ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.lg};
  text-align: center;
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing[2]};
`;

// ==================== COMPARISON TABLE ====================

const ComparisonSection = styled.section`
  padding: ${props => props.theme.spacing[12]} 0;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing[3]} 0;
  text-align: center;
`;

const SectionSubtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 auto ${props => props.theme.spacing[8]};
  text-align: center;
  max-width: 700px;
`;

const ComparisonTable = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.xl};
  overflow: hidden;
`;

const ComparisonHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  background: ${props => props.theme.colors.backgroundTertiary};
  padding: ${props => props.theme.spacing[4]};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  border-bottom: 1px solid ${props => props.theme.colors.border};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const ComparisonRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  padding: ${props => props.theme.spacing[4]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  align-items: center;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${props => props.theme.colors.backgroundTertiary}40;
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing[3]};
    padding: ${props => props.theme.spacing[5]};
    background: ${props => props.theme.colors.backgroundTertiary};
    margin-bottom: ${props => props.theme.spacing[3]};
    border-radius: ${props => props.theme.borders.radius.lg};
  }
`;

const FeatureName = styled.div`
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    margin-bottom: ${props => props.theme.spacing[2]};
  }
`;

const FeatureValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};

  .mobile-label {
    display: none;
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    justify-content: space-between;
    width: 100%;
    .mobile-label {
      display: inline;
      color: ${props => props.theme.colors.textSecondary};
    }
  }
`;

const CheckIcon = styled(Check)`
  color: ${props => props.theme.colors.primary};
`;

const XIcon = styled(X)`
  color: ${props => props.theme.colors.error};
`;

// ==================== BENEFITS SECTION ====================

const BenefitsSection = styled.section`
  padding: ${props => props.theme.spacing[12]} 0;
  background: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borders.radius.xl};
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing[6]};
  margin-top: ${props => props.theme.spacing[8]};

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[3]};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.lg};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const BenefitIconWrapper = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${props => props.theme.borders.radius.md};
  background: ${props => props.$color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
`;

const BenefitTitle = styled.h3`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const BenefitDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;
`;

// ==================== FAQ SECTION ====================

const FAQSection = styled.section`
  padding: ${props => props.theme.spacing[12]} 0;
`;

const FAQList = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[4]};
  margin-top: ${props => props.theme.spacing[8]};
`;

const FAQItem = styled.div<{ $isOpen: boolean }>`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.$isOpen ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing[5]};
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }

  svg {
    flex-shrink: 0;
    transition: transform 0.3s ease;
  }
`;

const FAQAnswer = styled(motion.div)`
  padding: 0 ${props => props.theme.spacing[5]} ${props => props.theme.spacing[5]};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.base};
  line-height: 1.6;
`;

// ==================== COMPONENT ====================

interface FAQ {
  question: string;
  answer: string;
}

interface Plan {
  id: string;
  name: string;
  oldPrice: string;
  price: string;
  period: string;
  discount: string;
  installment?: string;
  description: string;
  features: string[];
  savings: string;
  featured?: boolean;
}

const Upgrade: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { status: subscriptionStatus } = useSubscriptionStatusContext();
  const [openFAQIndex, setOpenFAQIndex] = useState<number | null>(null);

  const currentPlan = subscriptionStatus.plano?.toLowerCase() || '';
  const hasSubscription = !!currentPlan && currentPlan !== 'null' && currentPlan !== '';

  const plans: Plan[] = [
    {
      id: 'mensal',
      name: 'Enterprise Mensal',
      oldPrice: 'R$ 599',
      price: 'R$ 499',
      period: '/mês',
      discount: '17% OFF',
      description: 'Ideal para começar e testar todos os recursos premium.',
      features: [
        '1000 Mensagens WhatsApp semanais',
        'Modelo de IA: Opus 4.1',
        'Painel de controle',
        'Dados em tempo real',
        'Contém 4 Integrações',
        'Suporte prioritário 24/7',
        'Todas atualizações Loopra',
      ],
      savings: 'Economize R$ 100/mês',
      featured: false
    },
    {
      id: 'trimestral',
      name: 'Enterprise Trimestral',
      oldPrice: 'R$ 1.497',
      price: 'R$ 1.197',
      period: '/trimestre',
      discount: '20% OFF',
      installment: '3x de R$ 399 sem juros',
      description: 'Melhor custo-benefício para empresas em crescimento.',
      features: [
        'Tudo do Mensal +',
        '4000 Mensagens WhatsApp semanais',
        'Editor de scripts com IA',
        'Painel de controle com Multi-Acesso',
        'Contém +4 Integrações',
        'Bot com suporte a Mídias',
      ],
      savings: 'Economize R$ 300 no trimestre',
      featured: true
    },
    {
      id: 'anual',
      name: 'Enterprise Anual',
      oldPrice: 'R$ 5.988',
      price: 'R$ 3.997',
      period: '/ano',
      discount: '33% OFF',
      installment: '12x de R$ 333 sem juros',
      description: 'Máxima economia e benefícios para o ano todo.',
      features: [
        'Tudo do Trimestral +',
        'Mensagens Ilimitadas WhatsApp',
        'Contém Todas as Integrações',
        'Prioridade em novos recursos',
        'Treinamento avançado de Uso',
        'Notificações a Intervenção Humana',
        'Re-marketing Automatizado',
      ],
      savings: 'Economize R$ 1.991 no ano',
      featured: false
    }
  ];

  const comparisonFeatures = [
    { name: 'Mensagens WhatsApp', mensal: '1000 / semana', trimestral: '4000 / semana', anual: 'Ilimitadas' },
    { name: 'Modelo de IA', mensal: 'Opus 4.1', trimestral: 'Opus 4.1', anual: 'Opus 4.1' },
    { name: 'Dados em tempo real', mensal: true, trimestral: true, anual: true },
    { name: 'Editor de Scripts com IA', mensal: false, trimestral: true, anual: true },
    { name: 'Painel Multi-Acesso', mensal: false, trimestral: true, anual: true },
    { name: 'Integrações', mensal: 'Até 4', trimestral: 'Até 8', anual: 'Todas' },
    { name: 'Bot com Suporte a Mídias', mensal: false, trimestral: true, anual: true },
    { name: 'Suporte', mensal: 'Prioritário 24/7', trimestral: 'Prioritário 24/7', anual: 'VIP com canal direto' },
    { name: 'Acesso a Novos Recursos', mensal: false, trimestral: false, anual: 'Prioritário' },
    { name: 'Treinamento Avançado', mensal: false, trimestral: false, anual: true },
    { name: 'Notificação para Intervenção Humana', mensal: false, trimestral: false, anual: true },
    { name: 'Re-marketing Automatizado', mensal: false, trimestral: false, anual: true },
  ];

  const benefits = [
    {
      icon: <BarChart3 size={24} />,
      color: theme.colors.primary,
      title: 'Análises Avançadas',
      description: 'Dashboards interativos e relatórios detalhados em tempo real para tomar decisões baseadas em dados.'
    },
    {
      icon: <Shield size={24} />,
      color: theme.colors.primary,
      title: 'Segurança',
      description: 'Criptografia de ponta a ponta e proteção completa para garantir a segurança dos seus dados.'
    },
    {
      icon: <Users size={24} />,
      color: theme.colors.primary,
      title: 'Painel Multi-Usuário',
      description: 'Colabore com sua equipe com nosso painel de controle multi-acesso, disponível nos planos avançados.'
    },
    {
      icon: <Headphones size={24} />,
      color: theme.colors.warning,
      title: 'Suporte 24/7',
      description: 'Atendimento prioritário via chat, email e telefone para garantir que você nunca fique na mão.'
    },
    {
      icon: <Zap size={24} />,
      color: theme.colors.warning,
      title: 'Automações com IA',
      description: 'Automatize tarefas repetitivas com o poder do modelo Opus 4.1 e economize horas de trabalho.'
    },
    {
      icon: <Globe size={24} />,
      color: theme.colors.primary,
      title: 'Ecossistema Conectado',
      description: 'Conecte-se com as principais plataformas como WhatsApp, Instagram e Facebook para centralizar sua operação.'
    },
    {
      icon: <TrendingUp size={24} />,
      color: theme.colors.primary,
      title: 'Aumento de Performance',
      description: 'Aumente os resultados do seu negócio com funis otimizados e automações inteligentes.'
    },
    {
      icon: <Code size={24} />,
      color: theme.colors.primary,
      title: 'Editor de Scripts com IA',
      description: 'Crie e personalize fluxos de automação complexos com nosso editor de scripts inteligente.'
    },
    {
      icon: <Smartphone size={24} />,
      color: theme.colors.primary,
      title: 'Notificações Inteligentes',
      description: 'Receba alertas para intervenção humana e acompanhe as interações importantes em tempo real.'
    }
  ];

  const faqs: FAQ[] = [
    {
      question: 'Posso trocar de plano a qualquer momento?',
      answer: 'Sim! Você pode fazer upgrade ou downgrade do seu plano a qualquer momento. O valor será ajustado proporcionalmente na sua fatura.'
    },
    {
      question: 'Os meus dados estão seguros?',
      answer: 'Absolutamente. Utilizamos criptografia de ponta a ponta e as melhores práticas de segurança de servidores para garantir que seus dados estejam sempre protegidos.'
    },
    {
      question: 'Quais são os principais limites dos planos?',
      answer: 'Nossos planos são projetados para escalar com seu negócio. Os limites variam principalmente no número de mensagens de WhatsApp e no nível de acesso a funcionalidades avançadas como o Editor de Scripts. Não há limite de leads ou vendas.'
    },
    {
      question: 'Como funciona o suporte prioritário?',
      answer: 'Todos os nossos planos contam com suporte prioritário 24/7. O plano Anual oferece um canal de suporte VIP direto para um atendimento ainda mais rápido e personalizado.'
    },
    {
      question: 'Posso cancelar quando quiser?',
      answer: 'Sim, você pode cancelar sua assinatura a qualquer momento, sem taxas ou multas. Você continuará com acesso até o fim do seu ciclo de pagamento.'
    },
    {
      question: 'As integrações têm custo adicional?',
      answer: 'Não. Todas as integrações disponíveis em cada plano estão incluídas no valor da assinatura, sem custos extras.'
    }
  ];

  const handleSelectPlan = (planId: string) => {
    if (hasSubscription) {
      alert('Você já possui uma assinatura ativa! Entre em contato com o suporte para alterar seu plano.');
      return;
    }

    // TODO: Integrar com o sistema de pagamento
    alert(`Funcionalidade de pagamento em desenvolvimento. Plano selecionado: ${planId}`);
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQIndex(openFAQIndex === index ? null : index);
  };

  const renderFeatureValue = (value: string | boolean) => {
    if (typeof value === 'boolean') {
      return value ? <CheckIcon size={20} /> : <XIcon size={20} />;
    }
    if (value === 'infinity') {
      return <Infinity size={20} />;
    }
    return value;
  };

  return (
    <PageContainer>
      <Container>
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          Voltar
        </BackButton>

        {/* Hero Section */}
        <HeroSection>
          <HeroContent>
            <HeroTitle>
              Desbloqueie o próximo nível do seu negócio
            </HeroTitle>
            <HeroSubtitle>
              Escale seu negócio com automação inteligente, integrações ilimitadas e suporte dedicado. Desbloqueie todo o potencial da sua operação com nossos recursos premium.
            </HeroSubtitle>
            <TrustBadges>
              <TrustBadge>
                <Zap size={20} />
                Acesso Imediato
              </TrustBadge>
              <TrustBadge>
                <Shield size={20} />
                Pagamento seguro
              </TrustBadge>
              <TrustBadge>
                <Clock size={20} />
                Cancelamento livre
              </TrustBadge>
            </TrustBadges>
          </HeroContent>
        </HeroSection>

        {/* Pricing Section */}
        <PricingSection>
          <PlansGrid>
            {plans.map((plan, index) => (
              <PlanCard
                key={plan.id}
                $featured={plan.featured}
                $disabled={hasSubscription}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                {plan.featured && (
                  <PopularBadge>
                    <Crown size={16} fill="currentColor" />
                    MAIS ESCOLHIDO
                  </PopularBadge>
                )}

                {plan.id === 'trimestral' && (
                  <SavingsCornerBadge $color="#3b82f6">
                    <Zap size={14} fill="currentColor" />
                    Economize 40%
                  </SavingsCornerBadge>
                )}

                {plan.id === 'anual' && (
                  <SavingsCornerBadge $color="#f59e0b">
                    <Sparkles size={14} fill="currentColor" />
                    Economize 63%
                  </SavingsCornerBadge>
                )}

                <PlanHeader>
                  <PlanName>
                    {plan.id === 'mensal' ? 'Mensal' : plan.id === 'trimestral' ? 'Trimestral' : 'Anual'}
                  </PlanName>

                  <PriceContainer>
                    {plan.oldPrice && <OldPrice>{plan.id === 'mensal' ? 'R$ 69,90' : plan.id === 'trimestral' ? 'R$ 89,90' : 'R$ 49,90'}</OldPrice>}
                    <PriceRow>
                      <Price>
                        {plan.id === 'mensal' ? 'R$ 49,90' : plan.id === 'trimestral' ? '3x R$ 34,11' : 'R$ 18,59'}
                      </Price>
                      {plan.id === 'anual' && <PricePeriod>/mês</PricePeriod>}
                    </PriceRow>

                    {plan.id !== 'mensal' && (
                      <InstallmentInfo>
                        <span>sem juros</span> • ou R$ {plan.id === 'trimestral' ? '89,90' : '159,90'} à vista
                      </InstallmentInfo>
                    )}
                    {plan.id === 'mensal' && (
                      <InstallmentInfo>por mês</InstallmentInfo>
                    )}

                    <PlanDescription>
                      {plan.id === 'mensal' ? 'Perfeito para começar' : plan.id === 'trimestral' ? 'Melhor custo-benefício' : 'Máximo desconto'}
                    </PlanDescription>
                  </PriceContainer>
                </PlanHeader>

                <FeaturesList>
                  {plan.features.map((feature, idx) => (
                    <FeatureItem key={idx}>
                      <FeatureIcon>
                        <Check size={20} />
                      </FeatureIcon>
                      <FeatureText>{feature}</FeatureText>
                    </FeatureItem>
                  ))}
                </FeaturesList>

                <SavingsBadge>
                  <Target size={16} />
                  {plan.savings}
                </SavingsBadge>

                <PlanButton
                  $variant={plan.featured ? 'primary' : 'secondary'}
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={hasSubscription}
                  aria-label={`Selecionar plano ${plan.name}`}
                >
                  {hasSubscription ? 'Assinatura Ativa' : 'Começar Agora'}
                  {!hasSubscription && <Zap size={16} />}
                </PlanButton>
              </PlanCard>
            ))}
          </PlansGrid>
        </PricingSection>

        {/* Comparison Table */}
        <ComparisonSection>
          <SectionTitle>Comparação Detalhada de Planos</SectionTitle>
          <SectionSubtitle>
            Todos os planos incluem recursos premium. Veja qual oferece o melhor custo-benefício para você.
          </SectionSubtitle>
          <ComparisonTable>
            <ComparisonHeader>
              <div>Recurso</div>
              <div style={{ textAlign: 'center' }}>Mensal</div>
              <div style={{ textAlign: 'center' }}>Trimestral</div>
              <div style={{ textAlign: 'center' }}>Anual</div>
            </ComparisonHeader>
            {comparisonFeatures.map((feature, idx) => (
              <ComparisonRow key={idx}>
                <FeatureName>{feature.name}</FeatureName>
                <FeatureValue>
                  <span className="mobile-label">Mensal</span>
                  {renderFeatureValue(feature.mensal)}
                </FeatureValue>
                <FeatureValue>
                  <span className="mobile-label">Trimestral</span>
                  {renderFeatureValue(feature.trimestral)}
                </FeatureValue>
                <FeatureValue>
                  <span className="mobile-label">Anual</span>
                  {renderFeatureValue(feature.anual)}
                </FeatureValue>
              </ComparisonRow>
            ))}
          </ComparisonTable>
        </ComparisonSection>

        {/* Benefits Section */}
        <BenefitsSection>
          <Container>
            <SectionTitle>Todos os Recursos Premium Incluídos</SectionTitle>
            <SectionSubtitle>
              Tenha acesso completo a todas as ferramentas que você precisa para transformar seu negócio
            </SectionSubtitle>
            <BenefitsGrid>
              {benefits.map((benefit, idx) => (
                <BenefitCard key={idx}>
                  <BenefitIconWrapper $color={benefit.color}>
                    {benefit.icon}
                  </BenefitIconWrapper>
                  <BenefitTitle>{benefit.title}</BenefitTitle>
                  <BenefitDescription>{benefit.description}</BenefitDescription>
                </BenefitCard>
              ))}
            </BenefitsGrid>
          </Container>
        </BenefitsSection>

        {/* FAQ Section */}
        <FAQSection>
          <SectionTitle>Perguntas Frequentes</SectionTitle>
          <SectionSubtitle>
            Tire todas as suas dúvidas sobre os planos e recursos
          </SectionSubtitle>
          <FAQList>
            {faqs.map((faq, idx) => (
              <FAQItem key={idx} $isOpen={openFAQIndex === idx}>
                <FAQQuestion
                  onClick={() => toggleFAQ(idx)}
                  aria-expanded={openFAQIndex === idx}
                  aria-controls={`faq-answer-${idx}`}
                >
                  {faq.question}
                  {openFAQIndex === idx ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </FAQQuestion>
                <AnimatePresence>
                  {openFAQIndex === idx && (
                    <FAQAnswer
                      id={`faq-answer-${idx}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {faq.answer}
                    </FAQAnswer>
                  )}
                </AnimatePresence>
              </FAQItem>
            ))}
          </FAQList>
        </FAQSection>
      </Container>
    </PageContainer>
  );
};

export default Upgrade;